import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  selectAllItems,
  selectIsLoading,
} from "@/modules/ticket/ticketQueue/ticketQueueSlice";
import { showNotificationMessage } from "@/modules/uiState";
import { useAppDispatch } from "@/store/store";

/**
 * Hook to show toast notifications for queue sync events
 *
 * Notifications:
 * - Sync started (when items begin processing)
 * - Sync completed (all items successful)
 * - Sync failed (one or more items failed)
 */
export function useQueueNotifications() {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const items = useSelector(selectAllItems);
  const isLoading = useSelector(selectIsLoading);

  // Track previous state to detect changes
  const prevItemsCountRef = useRef(items.length);
  const prevLoadingRef = useRef(isLoading);
  const prevErrorCountRef = useRef(0);

  useEffect(() => {
    const errorCount = items.filter((item) => item.status === "error").length;
    const pendingCount = items.filter(
      (item) => item.status === "pending",
    ).length;

    // Sync started
    if (!prevLoadingRef.current && isLoading && pendingCount > 0) {
      dispatch(
        showNotificationMessage({
          messageId: "queue.notification.syncStarted",
          values: { count: pendingCount },
        }),
      );
    }

    // Sync completed successfully
    if (
      prevLoadingRef.current &&
      !isLoading &&
      prevItemsCountRef.current > items.length &&
      errorCount === 0
    ) {
      const syncedCount = prevItemsCountRef.current - items.length;
      dispatch(
        showNotificationMessage({
          messageId: "queue.notification.syncSuccess",
          values: { count: syncedCount },
        }),
      );
    }

    // Sync failed (new errors appeared)
    if (
      prevLoadingRef.current &&
      !isLoading &&
      errorCount > prevErrorCountRef.current
    ) {
      const newErrorCount = errorCount - prevErrorCountRef.current;
      dispatch(
        showNotificationMessage({
          messageId: "queue.notification.syncFailed",
          values: { count: newErrorCount },
        }),
      );
    }

    // Update refs
    prevItemsCountRef.current = items.length;
    prevLoadingRef.current = isLoading;
    prevErrorCountRef.current = errorCount;
  }, [items, isLoading, dispatch, intl]);
}
